import { queryOptions, useQuery } from '@tanstack/react-query'
import api from '../api/client'
import type { CatalogCourseDetail, CatalogDeckListResponse, CatalogListResponse } from '../types'

export interface CatalogFilters {
  language?: string
  level?: string | null
  topic?: string | null
  page?: number
  pageSize?: number
  enabled?: boolean
}

/**
 * Shared with the neighbour prefetch; `abortable` is for the prefetch only (see
 * useProgress). Pro-only on the server (`require_pro_plan`): the prefetch asks
 * for it only for a learner with Pro or a trial.
 */
export const catalogCoursesQuery = (filters: CatalogFilters = {}, abortable = false) => {
  const { language = 'en', level, topic, page = 1, pageSize = 24, enabled = true } = filters
  return queryOptions({
    queryKey: ['catalog', 'courses', language, level ?? null, topic ?? null, page],
    queryFn: async (ctx) => {
      const params = new URLSearchParams({ language, page: String(page), page_size: String(pageSize) })
      if (level) params.set('level', level)
      if (topic) params.set('topic', topic)
      const { data } = await api.get<CatalogListResponse>(`/api/v1/catalog/courses?${params}`, abortable ? { signal: ctx.signal } : undefined)
      return data
    },
    // 5-minute stale time — catalog doesn't change often
    staleTime: 5 * 60 * 1000,
    enabled,
  })
}

export function useCatalogCourses(filters: CatalogFilters = {}) {
  return useQuery(catalogCoursesQuery(filters))
}

export interface CatalogDeckFilters {
  level?: string | null
  page?: number
  pageSize?: number
  enabled?: boolean
}

/** Shared with the neighbour prefetch; `abortable` is for the prefetch only (see useProgress). */
export const catalogDecksQuery = (filters: CatalogDeckFilters = {}, abortable = false) => {
  const { level, page = 1, pageSize = 30, enabled = true } = filters
  return queryOptions({
    queryKey: ['catalog', 'decks', level ?? null, page],
    queryFn: async (ctx) => {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
      if (level) params.set('level', level)
      const { data } = await api.get<CatalogDeckListResponse>(`/api/v1/catalog/decks?${params}`, abortable ? { signal: ctx.signal } : undefined)
      return data
    },
    staleTime: 5 * 60 * 1000,
    enabled,
  })
}

export function useCatalogDecks(filters: CatalogDeckFilters = {}) {
  return useQuery(catalogDecksQuery(filters))
}

export function useCatalogCourse(id: string) {
  return useQuery({
    queryKey: ['catalog', 'courses', id],
    queryFn: async () => {
      const { data } = await api.get<CatalogCourseDetail>(`/api/v1/catalog/courses/${id}`)
      return data
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}
